/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useEffect, useState } from 'react';
import BackButton from '../common/BackButton';
import { useNavigate } from 'react-router-dom';
import { Participant, Company, ParticipantType } from '../../types';
import { apiClient } from '../../services/api';
import CompanyModal from '../common/CompanyModal';
import ParticipantModal from '../common/ParticipantModal';
import './ParticipantList.css';

const ParticipantList: React.FC = () => {
  const navigate = useNavigate();
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);

  useEffect(() => {
    fetchParticipants();
  }, []);

  const fetchParticipants = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getAllParticipants();
      if (response.success && response.participants) {
        setParticipants(response.participants);
      } else {
        setError(response.error || 'Failed to load participants');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while loading participants');
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = (type: ParticipantType) => {
    switch (type) {
      case ParticipantType.USER:
        return 'User';
      case ParticipantType.MANAGER:
        return 'Manager';
      case ParticipantType.ADMIN:
        return 'Admin';
      default:
        return type;
    }
  };

  const getTypeClass = (type: ParticipantType) => {
    switch (type) {
      case ParticipantType.USER:
        return 'type-user';
      case ParticipantType.MANAGER:
        return 'type-manager';
      case ParticipantType.ADMIN:
        return 'type-admin';
      default:
        return '';
    }
  };

  const handleCompanyClick = (company: Company) => {
    setSelectedCompany(company);
  };

  const handleParticipantClick = (participant: Participant) => {
    setSelectedParticipant(participant);
  };

  const closeCompanyModal = () => {
    setSelectedCompany(null);
  };

  const closeParticipantModal = () => {
    setSelectedParticipant(null);
  };

  if (loading) {
    return (
      <div className="participant-list-container">
      <BackButton to="/" />
        <div className="loading">Loading participants...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="participant-list-container">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={fetchParticipants} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="participant-list-container">
      <div className="participant-list-header">
        <div className="header-left">
          <h1>Participants</h1>
          <div className="participant-count">
            {participants.length} {participants.length === 1 ? 'Participant' : 'Participants'}
          </div>
        </div>
        <button
          className="create-participant-button"
          onClick={() => navigate('/participants/create')}
        >
          Create New Participant
        </button>
      </div>

      {participants.length === 0 ? (
        <div className="empty-state">
          <p>No participants found</p>
        </div>
      ) : (
        <div className="participant-table-container">
          <table className="participant-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Username</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Type</th>
                <th>Manager</th>
                <th>Company</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((participant) => (
                <tr
                  key={participant.id}
                  onClick={() => navigate(`/participants/${participant.uuid}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{participant.id}</td>
                  <td className="participant-username">{participant.username}</td>
                  <td>{participant.firstName || <span className="not-available">N/A</span>}</td>
                  <td>{participant.lastName || <span className="not-available">N/A</span>}</td>
                  <td>
                    <span className={`type-badge ${getTypeClass(participant.type)}`}>
                      {getTypeLabel(participant.type)}
                    </span>
                  </td>
                  <td>
                    {participant.manager ? (
                      <button
                        className="link-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleParticipantClick(participant.manager!);
                        }}
                      >
                        {participant.manager.username}
                      </button>
                    ) : (
                      <span className="not-available">N/A</span>
                    )}
                  </td>
                  <td>
                    {participant.company ? (
                      <button
                        className="link-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCompanyClick(participant.company!);
                        }}
                      >
                        {participant.company.name}
                      </button>
                    ) : (
                      <span className="not-available">N/A</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedCompany && (
        <CompanyModal company={selectedCompany} onClose={closeCompanyModal} />
      )}

      {selectedParticipant && (
        <ParticipantModal participant={selectedParticipant} onClose={closeParticipantModal} />
      )}
    </div>
  );
};

export default ParticipantList;
